import React, { useState } from "react";

import { CellProps } from "react-table";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { computeRowTextColor, getBooleanDisplayIcon } from "../../../helpers/utils/Utils";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import { SearchSelectWithEnum } from "../../../components/common/datatable/filter/SearchSelectWrapper";
import { SearchSelectWithIndeterminateBoolean } from "../../../components/common/datatable/DataTableUtils";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { useSearchCannedNotesQuery } from "../../data/cannedNotes/cannedNotesApi";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";

interface NoteRow {
    id: string;
    noteNumber: number;
    comment: string;
    type: string;
    bold: boolean;
    yellowHighlight: boolean;
    redColor: boolean;
}

interface CannedNoteSelectorProps {
    setNoteBody: (body: string) => void;
    setBold: any;
    formNoteType: string;
    type: string | null;
    cannedNoteType: string | null;
    setYellowHighlight: any;
    setRedColor: any;
    bold?: any
}

const CannedNoteSelector: React.FC<CannedNoteSelectorProps> = ({
    setNoteBody,
    setBold,
    bold,
    formNoteType,
    type,
    cannedNoteType,
    setYellowHighlight,
    setRedColor
}) => {
    const { data: enums } = useGetEnumsQuery("");
    const [selectedId, setSelectedId] = useState<any>(null);

    const setCanned = (row: { original: NoteRow }) => {
        setNoteBody(row.original.comment);
        setBold?.(row.original.bold);
        setYellowHighlight?.(row.original.yellowHighlight);
        setRedColor?.(row.original.redColor);
    };

    const handleRowSelection = (newSelectedRow: any, row: any) => {
        setCanned(row)
        setSelectedId(row?.original?.id);
    };


    const SetCannedSelector = (props: CellProps<NoteRow, any>): JSX.Element => {
        React.useEffect(() => {
            const timeout = setTimeout(() => {
                const el = document.getElementById(`${props.row.original.id}1`);

                if (el) {
                    const tr = el.closest("tr");
                    const table = el.closest("table");

                    if (tr) {
                        tr.setAttribute("id", props.row.original.id);

                        // Always clean up styles first
                        tr.classList.remove("!bg-[#fffd80]", "border-b", "border-surface-5");

                        if (props.row.original.yellowHighlight) {
                            tr.classList.add("!bg-[#fffd80]", "border-b", "border-surface-5");
                            table?.classList.remove("table-hover", "table-striped");
                        }
                    }
                }
            }, 100); // slight delay is usually fine; 100ms works better than 500ms

            return () => clearTimeout(timeout);
        }, [props.row.original]);

        return <span id={`${props.row.original.id}1`}>{props.value}</span>;
    };


    const columns: DataTableColumn[] = [

        {
            label: "Note No.", accessor: "noteNumber", visibility: { hasShowSetting: false },
            cell: (props: any) => {
                return SetCannedSelector(props);
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"number"} placeholder={"Enter Note No..."}
                    name={"noteNumber"}
                    accessor={"noteNumber"} />,
                overrideFilterLabel: "Note No.",
                overrideFilterAccessor: "noteNumber"
            }]
        },

        {
            label: "Message", accessor: "comment", visibility: { hasShowSetting: false },
            cell: (props: any) => {
                return <span
                    className={computeRowTextColor(props.row.original.redColor, props.row.original.bold)}>{props.value}</span>;
            },

            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Note..."}
                    name={"message"} accessor={"message"} />,
                overrideFilterLabel: "Message",
                overrideFilterAccessor: "message"
            }]
        },

        {
            label: "Type", accessor: "type",
            cell: (props: any) => {
                return <>
                    <span>{getEnumDisplayNameByConstant(enums?.["CertificateNoteType"], props.value)}</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["CertificateNoteType"], "Select Note Type(es)...", true, "type"),
                overrideFilterLabel: "Type",
            }],
        },

        {
            label: "Is Bold", accessor: "bold", visibility: { canColumnShow: false },
            cell: (props: any) => {
                return <>
                    <span>{getBooleanDisplayIcon(props.value, "Bold", "Not Bold")}</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Is Bold...", "bold"),
                overrideFilterAccessor: "bold",
                overrideFilterLabel: "Is Bold",
            }]
        },

        {
            label: "Yellow Highlight", accessor: "yellowHighlight", visibility: { canColumnShow: false },
            cell: (props: any) => {
                return <>
                    <span>{getBooleanDisplayIcon(props.value, "True", "False")}</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Yellow Highlight...", "yellowHighlight"),
                overrideFilterAccessor: "yellowHighlight",
                overrideFilterLabel: "Yellow Highlight",
            }]
        },

        {
            label: "Red Color", accessor: "redColor", visibility: { canColumnShow: false },
            cell: (props: any) => {
                return <>
                    <span>{getBooleanDisplayIcon(props.value, "True", "False")}</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Red Color...", "redColor"),
                overrideFilterAccessor: "redColor",
                overrideFilterLabel: "Red Color",
            }]
        }



    ];

    return (
        <div className="m-0 p-0">
            <div className={formNoteType === "TAX" ? "table_data" : "table_data hoa_table"}>
                <div className="table-responsive jurisdictions_selector">
                    <DataTable
                        columns={columns}
                        defaultFilter={{ type: type, pageSize: 5, pageNumber: 0 }}
                        defaultSortOptions={{ accessor: 'creationDate', direction: 'asc' }}
                        appendFilterToUrl={false}
                        actions={{ fetchAction: useSearchCannedNotesQuery }}
                        renderOptions={{
                            skipUserSettingRender: true, skipFilterButtonRender: true,
                        }}
                        rowSelectionOptions={{
                            enableRowSelection: true,
                            onSelectionChange: handleRowSelection,
                            enableMultiRowSelection: false,
                            rowIdAccessor: 'id'
                        }}
                        isHoa={formNoteType === "HOA"}
                        classname={`${formNoteType === "HOA" ? 'bg-highlight-1 text-text-0' : 'border-highlight-0 bg-[steelblue] text-text-0'
                            }`}
                        defaultFilterFormExpanded={true}
                    />
                </div>
            </div>
        </div>
    );
};

export default CannedNoteSelector;